import { useState, useCallback, type ReactNode, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

interface BusinessInfoData {
  readonly businessName: string;
  readonly phone: string;
  readonly address: string;
  readonly hoursNote: string;
}

interface BusinessInfoStepProps {
  readonly initialName: string;
  readonly onComplete: (data: BusinessInfoData) => void;
}

const PHONE_REGEX = /^0[2-9]\d{7,8}$/;

export function BusinessInfoStep({ initialName, onComplete }: BusinessInfoStepProps): ReactNode {
  const [businessName, setBusinessName] = useState(initialName);
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    if (!businessName.trim()) {
      newErrors["businessName"] = "\u05E9\u05DD \u05E2\u05E1\u05E7 \u05D7\u05D5\u05D1\u05D4";
    }
    const cleanPhone = phone.replace(/[-\s]/g, "");
    if (!cleanPhone || !PHONE_REGEX.test(cleanPhone)) {
      newErrors["phone"] = "\u05DE\u05E1\u05E4\u05E8 \u05D8\u05DC\u05E4\u05D5\u05DF \u05DC\u05D0 \u05EA\u05E7\u05D9\u05DF";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [businessName, phone]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (!validate()) return;
      onComplete({
        businessName: businessName.trim(),
        phone: phone.replace(/[-\s]/g, ""),
        address: address.trim(),
        hoursNote: "\u05D0-\u05D4 09:00-18:00, \u05D5 08:00-14:00",
      });
    },
    [businessName, phone, address, validate, onComplete],
  );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 px-4 py-6">
      <h2 className="text-xl font-bold">{"\u05E4\u05E8\u05D8\u05D9 \u05D4\u05E2\u05E1\u05E7"}</h2>

      <Card>
        <CardContent className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="businessName" className="text-sm font-medium">
              {"\u05E9\u05DD \u05D4\u05E2\u05E1\u05E7"}
            </label>
            <Input
              id="businessName"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className="min-h-[44px] text-base"
              placeholder={"\u05E9\u05DD \u05D4\u05E2\u05E1\u05E7 \u05E9\u05DC\u05DA"}
            />
            {errors["businessName"] !== undefined && (
              <p className="text-sm text-destructive">{errors["businessName"]}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="phone" className="text-sm font-medium">
              {"\u05D8\u05DC\u05E4\u05D5\u05DF"}
            </label>
            <Input
              id="phone"
              type="tel"
              dir="ltr"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="min-h-[44px] text-base"
              placeholder="05X-XXXXXXX"
            />
            {errors["phone"] !== undefined && (
              <p className="text-sm text-destructive">{errors["phone"]}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="address" className="text-sm font-medium">
              {"\u05DB\u05EA\u05D5\u05D1\u05EA"}
            </label>
            <Input
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="min-h-[44px] text-base"
              placeholder={"\u05E8\u05D7\u05D5\u05D1 123, \u05EA\u05DC \u05D0\u05D1\u05D9\u05D1"}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">
              {"\u05E9\u05E2\u05D5\u05EA \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA"}
            </label>
            <p className="text-sm text-muted-foreground">
              {"\u05D1\u05E8\u05D9\u05E8\u05EA \u05DE\u05D7\u05D3\u05DC: \u05D0-\u05D4 09:00-18:00, \u05D5 08:00-14:00, \u05E9\u05D1\u05EA \u05E1\u05D2\u05D5\u05E8"}
            </p>
          </div>
        </CardContent>
      </Card>

      <Button type="submit" size="lg" className="min-h-[44px]">
        {"\u05D4\u05DE\u05E9\u05DA"}
      </Button>
    </form>
  );
}
